import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, Power, PowerOff, ListChecks } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TIPOS = [
  { id: "NAO_APROVACAO", label: "Não aprovação (na aprovação)" },
  { id: "CORTE", label: "Corte de estoque (na separação)" },
];

export default function Motivos() {
  const { user } = useAuth();
  const canExcluir = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const [motivos, setMotivos] = useState(null);
  const [novo, setNovo] = useState({ NAO_APROVACAO: "", CORTE: "" });

  const load = () => api.get("/motivos").then((r) => setMotivos(r.data));
  useEffect(() => { load(); }, []);

  const add = async (tipo) => {
    const label = (novo[tipo] || "").trim();
    if (!label) return;
    try {
      await api.post("/motivos", { tipo, label, ativo: true });
      setNovo((n) => ({ ...n, [tipo]: "" }));
      toast.success("Motivo adicionado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggle = async (m) => {
    try { await api.put(`/motivos/${m.id}`, { tipo: m.tipo, label: m.label, ativo: !m.ativo }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const del = async (m) => {
    if (!window.confirm(`Excluir o motivo "${m.label}"?`)) return;
    try { await api.delete(`/motivos/${m.id}`); toast.success("Motivo excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-5" data-testid="motivos-page">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Cadastros</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">Motivos</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Motivos usados na não aprovação de itens e no corte de estoque durante a separação. "Outro" sempre exige observação.</p>
      </div>

      {!motivos ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {TIPOS.map((t) => {
            const list = motivos.filter((m) => m.tipo === t.id);
            return (
              <Card key={t.id} className="bg-card/70 p-4" data-testid={`motivos-${t.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <ListChecks className="w-5 h-5 text-primary" />
                  <div className="font-display text-base font-bold uppercase">{t.label}</div>
                </div>
                <div className="space-y-1.5 mb-3">
                  {list.map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-2 border-b border-border pb-1.5" data-testid={`motivo-${m.id}`}>
                      <div className={`text-sm ${m.ativo ? "" : "line-through text-muted-foreground"}`}>{m.label}{m.protegido && <span className="ml-1.5 text-[10px] uppercase font-mono text-muted-foreground">(protegido)</span>}</div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggle(m)} data-testid={`toggle-motivo-${m.id}`}>
                          {m.ativo ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                        </Button>
                        {!m.protegido && canExcluir && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => del(m)} data-testid={`del-motivo-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Input value={novo[t.id]} onChange={(e) => setNovo((n) => ({ ...n, [t.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && add(t.id)} placeholder="Novo motivo" className="h-9" data-testid={`novo-motivo-${t.id}`} />
                  <Button size="icon" className="h-9 w-9 shrink-0" onClick={() => add(t.id)} data-testid={`add-motivo-${t.id}`}><Plus className="w-4 h-4" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
